import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard,
  PenLine,
  Table2,
  Users,
  LogOut,
  Wallet,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard Grafik", icon: LayoutDashboard, testid: "nav-grafik-link", end: true },
  { to: "/entry", label: "Entry Saldo", icon: PenLine, testid: "nav-entry-link" },
  { to: "/laporan", label: "Laporan Harian", icon: Table2, testid: "nav-laporan-link" },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
    isActive
      ? "bg-emerald-600 text-white"
      : "text-slate-300 hover:bg-slate-800 hover:text-white"
  }`;

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [...NAV];
  if (user?.role === "admin") {
    navItems.push({ to: "/admin/users", label: "Kelola User", icon: Users, testid: "nav-admin-link" });
  }

  const sidebarContent = (
    <>
      <div className="flex items-center gap-3 px-4 py-6 border-b border-slate-800">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-600">
          <Wallet className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="text-base font-bold text-white tracking-tight">SaldoPro 2026</p>
          <p className="text-xs text-slate-400">Posisi Saldo Harian</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {navItems.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={n.testid}>
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <p className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{user?.nama}</p>
        <p className="text-xs text-slate-400 capitalize mb-3" data-testid="sidebar-user-role">
          {user?.role === "admin" ? "Administrator" : "User Entry"}
        </p>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="flex w-full items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden md:flex w-64 flex-col bg-slate-900 fixed inset-y-0 left-0 z-30">
        {sidebarContent}
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2 px-4 py-3">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-600">
            <Wallet className="h-4 w-4 text-white" />
          </div>
          <p className="text-sm font-bold text-white">SaldoPro 2026</p>
          <button
            onClick={handleLogout}
            data-testid="logout-button-mobile"
            className="ml-auto text-slate-300 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-2">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-800"
                }`
              }
            >
              <n.icon className="h-3.5 w-3.5" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>

      <main className="flex-1 min-w-0 md:ml-64 pt-28 md:pt-0">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
